// Lib
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Immutable from 'immutable';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getGridSize } from '../../../../../utils/grid/gridSizeSelector';

// Selectors
import { getCurrentVisibleBoardId } from '../../../../../reducers/currentBoardId/currentBoardIdSelector';
import { getMeasurementsMap } from '../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { elementGraphSelector } from '../../../../selectors/elementGraphSelector';
import { getElements } from '../../../../selectors/elementsSelector';

// Actions
import { navigateToElement } from '../../../../../reducers/navigationActions';

// Hooks
import useOrderedImageElements from '../../useOrderedImageElements';

// Constants
import { KEY_CODES } from '../../../../../utils/keyboard/keyConstants';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type UseImageModalNavigationProps = {
    element: ImMNElement;
    enabled: boolean;
};

/**
 * Provides image-to-image navigation via buttons and keyboard arrows.
 */
export const useImageModalNavigation = ({ element, enabled }: UseImageModalNavigationProps) => {
    const dispatch = useDispatch();
    const currentBoardId = useSelector(getCurrentVisibleBoardId);
    const elements = useSelector(getElements);
    const elementGraph = useSelector(elementGraphSelector);
    const measurements = useSelector(getMeasurementsMap);
    const gridSize = useSelector(getGridSize);

    const orderedImageElements = useOrderedImageElements({
        currentBoardId,
        elements,
        elementGraph,
        measurements,
        gridSize,
    }) as Immutable.List<ImMNElement>;

    const currentImageId = getElementId(element);
    const imageIds = useMemo(() => orderedImageElements.map(getElementId).toList(), [orderedImageElements]);
    const currentIndex = imageIds.indexOf(currentImageId);
    const canNavigate = currentIndex >= 0 && imageIds.size >= 2;

    const navigateToImageAtIndex = useCallback(
        (imageIndex: number) => {
            const elementId = imageIds.get(imageIndex);
            if (!elementId) return;

            dispatch(navigateToElement({ elementId, params: undefined }));
        },
        [dispatch, imageIds],
    );

    const navigateToPreviousImage = useCallback(() => {
        if (!canNavigate) return;

        const prevIndex = currentIndex <= 0 ? imageIds.size - 1 : currentIndex - 1;
        navigateToImageAtIndex(prevIndex);
    }, [canNavigate, currentIndex, imageIds.size, navigateToImageAtIndex]);

    const navigateToNextImage = useCallback(() => {
        if (!canNavigate) return;

        const nextIndex = currentIndex >= imageIds.size - 1 ? 0 : currentIndex + 1;
        navigateToImageAtIndex(nextIndex);
    }, [canNavigate, currentIndex, imageIds.size, navigateToImageAtIndex]);

    // Keyboard navigation (left/right and up/down arrows)
    useEffect(() => {
        if (!enabled || !canNavigate) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.keyCode) {
                case KEY_CODES.LEFT_ARROW:
                case KEY_CODES.UP_ARROW:
                    event.preventDefault();
                    navigateToPreviousImage();
                    break;
                case KEY_CODES.RIGHT_ARROW:
                case KEY_CODES.DOWN_ARROW:
                    event.preventDefault();
                    navigateToNextImage();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [enabled, canNavigate, navigateToPreviousImage, navigateToNextImage]);

    return {
        canNavigate,
        orderedImageElements,
        navigateToPreviousImage,
        navigateToNextImage,
    };
};
